import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PageState } from "../../components/PageState";
import { useAuth } from "../../auth/AuthContext";
import { formatMoneyBR, formatNumberBR } from "../../utils/format";

type ReportPartner = {
  partnerId: number;
  name: string;
  pct: number;
  planned: number;
  actual: number;
  gap: number;
};

type ReportMonth = {
  month: number;
  budget: number;
  planned: number;
  realized: number;
  gap: number;
};

type ReportResponse = {
  year: number;
  warnings?: string[];
  months: ReportMonth[];
};

type ReportMonthDetail = {
  year: number;
  month: number;
  budget: number;
  plannedTotal: number;
  realizedTotal: number;
  gap: number;
  warnings?: string[];
  breakdownSocios: ReportPartner[];
};

type ReportPartnerDetail = {
  year: number;
  month: number;
  partnerId: number;
  partnerName: string;
  total: number;
  contributions: Array<{
    id: number;
    description: string;
    amount: number;
    occurredAt: string;
    competenceYear: number | null;
    competenceMonth: number | null;
  }>;
};

const monthLabels = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

const normalizeYearInput = (value: string) => value.replace(/\D/g, "").slice(0, 4);
const isValidYear = (value: string) => {
  if (value.length !== 4) return false;
  const year = Number(value);
  return !Number.isNaN(year) && year <= new Date().getFullYear() + 15;
};

export const ReportsCashNeed: React.FC = () => {
  const { client } = useAuth();
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState<ReportResponse | null>(null);
  const [monthDetail, setMonthDetail] = useState<ReportMonthDetail | null>(null);
  const [partnerDetail, setPartnerDetail] = useState<ReportPartnerDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [loadingPartner, setLoadingPartner] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [partnerError, setPartnerError] = useState<string | null>(null);

  const loadReport = useCallback(async () => {
    if (!isValidYear(year)) {
      setError("Ano invalido.");
      setData(null);
      setMonthDetail(null);
      setPartnerDetail(null);
      return;
    }
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ year });
    const response = await client.get(`/reports/cash-need?${params.toString()}`);
    if (!response.ok) {
      setError("Falha ao carregar relatorio.");
      setLoading(false);
      return;
    }
    const payload = (await response.json()) as ReportResponse;
    setData(payload ?? null);
    setMonthDetail(null);
    setPartnerDetail(null);
    setLoading(false);
  }, [client, year]);

  useEffect(() => { void loadReport(); }, [loadReport]);

  const loadMonthDetail = useCallback(async (month: number) => {
    if (!isValidYear(year)) return;
    setLoadingMonth(true);
    setDetailError(null);
    setPartnerDetail(null);
    const response = await client.get(`/reports/cash-need/${year}/${month}`);
    if (!response.ok) {
      setDetailError("Falha ao carregar detalhamento.");
      setLoadingMonth(false);
      return;
    }
    const payload = (await response.json()) as ReportMonthDetail;
    setMonthDetail(payload);
    setLoadingMonth(false);
  }, [client, year]);

  const loadPartnerDetail = useCallback(async (month: number, partnerId: number) => {
    if (!isValidYear(year)) return;
    setLoadingPartner(true);
    setPartnerError(null);
    const response = await client.get(`/reports/cash-need/${year}/${month}/partner/${partnerId}`);
    if (!response.ok) {
      setPartnerError("Falha ao carregar contribuicoes.");
      setLoadingPartner(false);
      return;
    }
    const payload = (await response.json()) as ReportPartnerDetail;
    setPartnerDetail(payload);
    setLoadingPartner(false);
  }, [client, year]);

  const monthRows = useMemo(() => data?.months ?? [], [data]);

  const totals = useMemo(
    () =>
      monthRows.reduce(
        (acc, m) => ({
          budget: acc.budget + m.budget,
          planned: acc.planned + m.planned,
          realized: acc.realized + m.realized,
          gap: acc.gap + m.gap,
        }),
        { budget: 0, planned: 0, realized: 0, gap: 0 }
      ),
    [monthRows]
  );

  return (
    <div className="container py-4">
      <h1 className="mb-3">Necessidade de Caixa</h1>

      <div className="card shadow-sm mb-3" style={{ borderTop: "3px solid #0d6efd" }}>
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-md-2">
              <label className="form-label form-label-sm mb-1">Ano</label>
              <input
                className="form-control form-control-sm"
                value={year}
                onChange={(e) => setYear(normalizeYearInput(e.target.value))}
              />
            </div>
            <div className="col-auto d-flex align-items-end">
              <button className="btn btn-primary btn-sm" onClick={loadReport}>Atualizar</button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <PageState loading={loading} error={error}>
        {data?.warnings?.length ? (
          <div className="alert alert-warning">
            {data.warnings.map((w) => <div key={w}>{w}</div>)}
          </div>
        ) : null}

        {monthRows.length > 0 && (
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <div className="card shadow-sm h-100" style={{ borderLeft: "4px solid #6c757d" }}>
                <div className="card-body">
                  <div className="text-muted small">Orcamento total</div>
                  <div className="fw-bold fs-5 mt-1">{formatMoneyBR(totals.budget)}</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card shadow-sm h-100" style={{ borderLeft: "4px solid #0d6efd" }}>
                <div className="card-body">
                  <div className="text-muted small">Planejado total</div>
                  <div className="fw-bold fs-5 mt-1 text-primary">{formatMoneyBR(totals.planned)}</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card shadow-sm h-100" style={{ borderLeft: "4px solid #198754" }}>
                <div className="card-body">
                  <div className="text-muted small">Realizado total</div>
                  <div className="fw-bold fs-5 mt-1 text-success">{formatMoneyBR(totals.realized)}</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card shadow-sm h-100" style={{ borderLeft: `4px solid ${totals.gap >= 0 ? "#198754" : "#dc3545"}` }}>
                <div className="card-body">
                  <div className="text-muted small">Gap total</div>
                  <div className={`fw-bold fs-5 mt-1 ${totals.gap >= 0 ? "text-success" : "text-danger"}`}>
                    {formatMoneyBR(totals.gap)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card shadow-sm mb-3">
          <div className="card-body">
            <h6 className="card-title">Resumo mensal</h6>
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th>Orcamento</th>
                    <th>Planejado</th>
                    <th>Realizado</th>
                    <th>Gap</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {monthRows.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-muted">Nenhum dado encontrado.</td></tr>
                  )}
                  {monthRows.map((month) => (
                    <tr key={month.month}>
                      <td className="fw-semibold">{monthLabels[month.month - 1]}</td>
                      <td>{formatMoneyBR(month.budget)}</td>
                      <td>{formatMoneyBR(month.planned)}</td>
                      <td>{formatMoneyBR(month.realized)}</td>
                      <td className={month.gap < 0 ? "text-danger" : ""}>{formatMoneyBR(month.gap)}</td>
                      <td className="text-end">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => loadMonthDetail(month.month)}
                        >
                          Detalhar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {(monthDetail || loadingMonth) && (
          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="mb-0">
                  Detalhe {monthDetail ? `${monthLabels[monthDetail.month - 1]}/${monthDetail.year}` : ""}
                </h6>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => { setMonthDetail(null); setPartnerDetail(null); }}
                >
                  Fechar
                </button>
              </div>
              {loadingMonth && <div className="text-muted">Carregando...</div>}
              {detailError && <div className="alert alert-danger">{detailError}</div>}
              {monthDetail && !loadingMonth && (
                <>
                  {monthDetail.warnings?.length ? (
                    <div className="alert alert-warning">
                      {monthDetail.warnings.map((w) => <div key={w}>{w}</div>)}
                    </div>
                  ) : null}
                  <div className="row g-3 mb-3">
                    <div className="col-md-3">
                      <div className="text-muted small">Orcamento</div>
                      <div className="fw-semibold">{formatMoneyBR(monthDetail.budget)}</div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-muted small">Planejado</div>
                      <div className="fw-semibold">{formatMoneyBR(monthDetail.plannedTotal)}</div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-muted small">Realizado</div>
                      <div className="fw-semibold">{formatMoneyBR(monthDetail.realizedTotal)}</div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-muted small">Gap</div>
                      <div className={`fw-semibold ${monthDetail.gap < 0 ? "text-danger" : ""}`}>
                        {formatMoneyBR(monthDetail.gap)}
                      </div>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th>Socio</th>
                          <th>%</th>
                          <th>Planejado</th>
                          <th>Realizado</th>
                          <th>Gap</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthDetail.breakdownSocios.map((partner) => (
                          <tr key={partner.partnerId}>
                            <td>{partner.name}</td>
                            <td>{formatNumberBR(partner.pct)}%</td>
                            <td>{formatMoneyBR(partner.planned)}</td>
                            <td>{formatMoneyBR(partner.actual)}</td>
                            <td className={partner.gap < 0 ? "text-danger" : ""}>{formatMoneyBR(partner.gap)}</td>
                            <td className="text-end">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => loadPartnerDetail(monthDetail.month, partner.partnerId)}
                              >
                                Ver contribuicoes
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {(partnerDetail || loadingPartner) && (
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="mb-0">
                  {partnerDetail
                    ? `Contribuicoes de ${partnerDetail.partnerName} — ${monthLabels[partnerDetail.month - 1]}/${partnerDetail.year}`
                    : "Carregando..."}
                </h6>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setPartnerDetail(null)}>
                  Fechar
                </button>
              </div>
              {loadingPartner && <div className="text-muted">Carregando...</div>}
              {partnerError && <div className="alert alert-danger">{partnerError}</div>}
              {partnerDetail && !loadingPartner && (
                <>
                  <div className="mb-3">
                    <div className="text-muted small">Total realizado</div>
                    <div className="fw-bold fs-5">{formatMoneyBR(partnerDetail.total)}</div>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Descricao</th>
                          <th className="text-end">Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partnerDetail.contributions.length === 0 && (
                          <tr><td colSpan={3} className="text-center text-muted">Nenhuma contribuicao no mes.</td></tr>
                        )}
                        {partnerDetail.contributions.map((item) => (
                          <tr key={item.id}>
                            <td>{new Date(item.occurredAt).toLocaleDateString("pt-BR")}</td>
                            <td>{item.description}</td>
                            <td className="text-end">{formatMoneyBR(item.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </PageState>
    </div>
  );
};
